import { memo, useState, useCallback, useEffect, useMemo } from "react";
import clsx from "clsx";
import { CircleIcon } from "@phosphor-icons/react";
import { useShallow } from "zustand/shallow";
import { Button, Tooltip } from "@heroui/react";

import { useBoundStore } from "@/app/store";
import { FetchedTag } from "@/services/api/fetchers/tags";
import { TagAction } from "../types";
import { ICON_SIZE } from "@/config/constants";
import { getTagColorClassName } from "../utils";

const LONG_PRESS_DURATION = 350; // milliseconds

export interface TagCardProps {
  actions: (tag: FetchedTag) => TagAction[];
  "data-tag-index"?: number;
  className?: string;
  tag: FetchedTag;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  onFocus?: () => void;
  onPress: (wasKeyboard: boolean, ctrlKey?: boolean, longPress?: boolean) => void;
  tabIndex?: number;
}

export const TagCard = memo(function TagCard({
  actions,
  className,
  "data-tag-index": dataIndex,
  tag,
  isSelected = false,
  isSelectionMode,
  onFocus,
  onPress,
}: TagCardProps) {
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [longPressTimeoutId, setLongPressTimeoutId] = useState<number | null>(null);

  const shouldRenderActions = useMemo(
    () => deviceType === "desktop" && (isFocused || isHovered) && !isSelectionMode,
    [deviceType, isFocused, isHovered, isSelectionMode]
  );

  const tagActions = useMemo(() => (shouldRenderActions ? actions(tag) : []), [actions, tag, shouldRenderActions]);

  const handlePressCancel = useCallback(() => {
    // Clear timeout on cancel
    if (longPressTimeoutId) {
      clearTimeout(longPressTimeoutId);
      setLongPressTimeoutId(null);
    }

    setLongPressTriggered(false);
  }, [longPressTimeoutId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (longPressTimeoutId) {
        clearTimeout(longPressTimeoutId);
      }
    };
  }, [longPressTimeoutId]);

  // Add scroll listener to cancel long press when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (longPressTimeoutId) {
        handlePressCancel();
      }
    };

    if (longPressTimeoutId) {
      // Listen to scroll events on the document and any parent scrollable containers
      document.addEventListener("scroll", handleScroll, true);
      document.addEventListener("touchmove", handleScroll, true);

      return () => {
        document.removeEventListener("scroll", handleScroll, true);
        document.removeEventListener("touchmove", handleScroll, true);
      };
    }
  }, [longPressTimeoutId, handlePressCancel]);

  const handleClick = (e: React.MouseEvent) => {
    if (longPressTriggered) {
      setLongPressTriggered(false);
      return;
    }
    onPress(false, e.ctrlKey || e.metaKey);
  };

  const handlePressStart = () => {
    if (deviceType === "mobile") {
      setLongPressTriggered(false);

      // Clear any existing timeout
      if (longPressTimeoutId) {
        clearTimeout(longPressTimeoutId);
      }

      // Set timeout to trigger long press automatically
      const timeoutId = setTimeout(() => {
        setLongPressTriggered(true);
        onPress(false, false, true); // long press
      }, LONG_PRESS_DURATION);

      setLongPressTimeoutId(timeoutId);
    }
  };

  const handlePressEnd = () => {
    if (deviceType === "mobile") {
      // Clear the timeout if press ends before duration
      if (longPressTimeoutId) {
        clearTimeout(longPressTimeoutId);
        setLongPressTimeoutId(null);
      }

      // If long press was already triggered, prevent normal click
      if (longPressTriggered) {
        setLongPressTriggered(false);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    handlePressCancel();
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onPress(true, e.ctrlKey || e.metaKey);
      }
    },
    [onPress]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    const isDescendant = (parent: HTMLElement, child: HTMLElement) => {
      let node = child.parentNode;
      while (node != null) {
        if (node === parent) {
          return true;
        }
        node = node.parentNode;
      }
      return false;
    };

    const currentTarget = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;

    if (!relatedTarget || !isDescendant(currentTarget, relatedTarget)) {
      setIsFocused(false);
    }
  }, []);

  return (
    <>
      <div
        role="button"
        className={clsx(
          "group relative cursor-pointer rounded-xl border transition-colors duration-200",
          "border-default-200 bg-content1 focus-visible:ring-primary-500 dark:border-default-100 focus:outline-none focus-visible:ring-2",
          "transition-opacity duration-200 hover:opacity-80",
          {
            "border-primary dark:border-primary border-3": isSelected,
          },
          className
        )}
        data-tag-index={dataIndex}
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseEnter={() => setIsHovered(true)}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handlePressCancel}
      >
        <div className="flex h-full min-h-11 items-center gap-3 px-3">
          <CircleIcon
            className={clsx("shrink-0", getTagColorClassName(tag.color))}
            weight="duotone"
            size={ICON_SIZE.md}
          />

          <span className="line-clamp-1 flex-1">{tag.name}</span>

          {deviceType === "desktop" && (
            <div
              className={clsx("flex gap-1 transition-opacity duration-200 ease-out", "opacity-0", {
                "opacity-100": shouldRenderActions,
              })}
              tabIndex={-1}
            >
              {shouldRenderActions &&
                tagActions?.map((action) => (
                  <Tooltip key={action.key} content={action.label} placement="bottom" size="sm" className="shrink-0">
                    <Button isIconOnly size="sm" variant="light" onPress={() => action.run?.(tag)}>
                      {action.icon}
                    </Button>
                  </Tooltip>
                ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

export interface FocusableTagCardProps extends Omit<TagCardProps, "onPress" | "onFocus"> {
  index: number;
  isFocused: boolean;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  onFocus: (index: number) => void;
  onPress: (id: number, wasKeyboard?: boolean, ctrlKey?: boolean, longPress?: boolean) => void;
}

export const FocusableTagCard = memo(function FocusableTagCard({
  tag,
  index,
  isFocused,
  isSelected,
  isSelectionMode,
  onFocus,
  onPress,
  ...props
}: FocusableTagCardProps) {
  return (
    <TagCard
      {...props}
      tag={tag}
      data-tag-index={index}
      tabIndex={isFocused ? 0 : -1}
      isSelected={isSelected}
      isSelectionMode={isSelectionMode}
      onFocus={() => onFocus(index)}
      onPress={(wasKeyboard, ctrlKey, longPress) => onPress(tag.id, wasKeyboard, ctrlKey, longPress)}
    />
  );
});
